import type { BrandschutzklappeZeile } from '../../api';
import { OHNE_WERT, formatiereZahl, teilsumme, type BaumKnoten } from '../../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BRANDSCHUTZKLAPPEN - der Baum (TV8), ohne React
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Anlage → Ebene, die Klappen selbst stehen als Tabelle rechts daneben
 * (Muster `Baum`: „Die Blattebene ist Sache der Seite“). Die Ebene ist das
 * Blatt des Baums und trägt die Mengen; die Zahl an einer Anlage ist die
 * TEILSUMME ihrer Blätter (`teilsumme`), nicht eine zweite Zählung - so
 * kann die Anlage nie etwas anderes sagen als ihre Ebenen.
 *
 * „ohne Anlage“ ist ein eigener Ast (zuletzt), genau wie im Bericht LUE-BKL.
 * Ein Brandabschnitt ist im Bestand nirgends gepflegt; deshalb gliedert der
 * Baum nach Ebene. Die Reihenfolge ist die des Servers (Anlage, Ebenenindex).
 */

/** Was „ohne Anlage“ bzw. „ohne Ebene“ in der Auswahl des Berichts heißt (`?anlage=-`). */
export const OHNE = '-';

/** Die Auswahl eines Knotens, wie sie der Bericht versteht. */
export interface Klappenauswahl {
  /** Anlagenname oder {@link OHNE}. */
  anlage: string;
  /** Ebene oder {@link OHNE}; fehlt bei einer ganzen Anlage. */
  ebene?: string;
  /** Klartext, z. B. „RLT.T.EG.1 · G00“. */
  titel: string;
}

/** Die Daten eines Knotens: die Auswahl und - an den Blättern - die Mengen. */
export interface Klappenknoten {
  auswahl: Klappenauswahl;
  klappen: number;
  ohneAks: number;
}

function auswahlVon(z: BrandschutzklappeZeile, mitEbene: boolean): Klappenauswahl {
  const anlage = z.hatAnlage ? z.anlage : OHNE;
  if (!mitEbene) return { anlage, titel: z.anlage };
  return { anlage, ebene: z.hatEbene ? z.ebene : OHNE, titel: `${z.anlage} · ${z.ebene}` };
}

/** Ob eine Zeile zur Auswahl gehört - dieselbe Regel wie der Server (`LueftungBrandschutzklappen.Bilde`). */
export function gehoertZu(z: BrandschutzklappeZeile, a: Klappenauswahl | null): boolean {
  if (!a) return true;
  const anlage = z.hatAnlage ? z.anlage : OHNE;
  if (anlage !== a.anlage) return false;
  if (a.ebene === undefined) return true;
  return (z.hatEbene ? z.ebene : OHNE) === a.ebene;
}

export function klappenDerAuswahl<Z extends BrandschutzklappeZeile>(zeilen: readonly Z[], a: Klappenauswahl | null): Z[] {
  return zeilen.filter((z) => gehoertZu(z, a));
}

function mengentext(n: number): string {
  return `${formatiereZahl(n)} ${n === 1 ? 'Klappe' : 'Klappen'}`;
}

/** Der Baum Anlage → Ebene mit Teilsummen an jedem Knoten. */
export function klappenbaum(zeilen: readonly BrandschutzklappeZeile[]): BaumKnoten<Klappenknoten>[] {
  const anlagen = new Map<string, { kopf: BrandschutzklappeZeile; ebenen: Map<string, BrandschutzklappeZeile[]> }>();
  for (const z of zeilen) {
    const ak = z.hatAnlage ? `a:${z.anlage}` : 'a-';
    let a = anlagen.get(ak);
    if (!a) { a = { kopf: z, ebenen: new Map() }; anlagen.set(ak, a); }
    const ek = z.hatEbene ? `e:${z.ebene}` : 'e-';
    const liste = a.ebenen.get(ek);
    if (liste) liste.push(z); else a.ebenen.set(ek, [z]);
  }

  // „ohne Anlage“ zuletzt, die übrigen in der Folge des Servers.
  const geordnet = [...anlagen.entries()].sort(([x], [y]) => Number(x === 'a-') - Number(y === 'a-'));

  return geordnet.map(([, a], i) => {
    const kinder: BaumKnoten<Klappenknoten>[] = [...a.ebenen.entries()].map(([, liste], j) => ({
      id: `k${i}-${j}`,
      titel: liste[0].ebene,
      daten: {
        auswahl: auswahlVon(liste[0], true),
        klappen: liste.length,
        ohneAks: liste.filter((z) => z.aksSchluessel === null).length,
      },
    }));
    const knoten: BaumKnoten<Klappenknoten> = {
      id: `k${i}`,
      titel: a.kopf.anlage,
      daten: { auswahl: auswahlVon(a.kopf, false), klappen: 0, ohneAks: 0 },
      kinder,
    };
    return beschrifte(knoten);
  });
}

/** Menge und Lücke eines Knotens - beides Teilsummen der Blätter. Blätter beschriften sich selbst. */
function beschrifte(k: BaumKnoten<Klappenknoten>): BaumKnoten<Klappenknoten> {
  const kinder = k.kinder?.map(beschrifte);
  const mit = { ...k, kinder };
  const menge = teilsumme(mit, (d) => d.klappen, 'summe');
  const ohne = teilsumme(mit, (d) => d.ohneAks, 'summe');
  return {
    ...mit,
    wert: menge && menge.wert !== null ? mengentext(menge.wert) : OHNE_WERT,
    hinweis: ohne && ohne.wert ? `${formatiereZahl(ohne.wert)} ohne AKS` : undefined,
  };
}

/** Die Menge eines Knotens als Zahl (für Prüfungen und die Tafel): Teilsumme über die Blätter. */
export function knotenmenge(k: BaumKnoten<Klappenknoten>): number {
  return teilsumme(k, (d) => d.klappen, 'summe')?.wert ?? 0;
}
